import { Controller } from "@hotwired/stimulus"

// Handles drop of appointment cards onto calendar day columns.
//
// Time calculation from first principles:
//   - Grid lines are drawn at `h * HOUR_HEIGHT` px from the column top (line 40 of _calendar_week_grid)
//   - So pixel Y from column top → minutes = (y / HOUR_HEIGHT) * 60
//   - Card positions use `top: (minutes/1440)*100 %` which is equivalent
//   - The cursor grab offset (distance from cursor to card top at dragstart) is
//     subtracted so we compute time for the card's TOP edge, not the cursor
//   - Snapped to 15-minute increments via Math.round
//
// Visual behavior:
//   - On valid drop: card is repositioned optimistically, then background
//     grid refresh reconciles with server state
//   - On invalid drop: card is reverted to original position with error toast
//   - The original element is hidden during drag (by the draggable controller)
//     so there is no snap-back animation

const SNAP_MINUTES = 15
const TOTAL_MINUTES = 24 * 60
const DEFAULT_HOUR_HEIGHT = 64

export default class extends Controller {
  static values = {
    hourHeight: { type: Number, default: DEFAULT_HOUR_HEIGHT },
    date: String,
    reschedulePathTemplate: String,
    csrfToken: String,
  }

  connect() {
    this.element.addEventListener("dragover", this.dragover)
    this.element.addEventListener("dragleave", this.dragleave)
    this.element.addEventListener("drop", this.drop)
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.dragover)
    this.element.removeEventListener("dragleave", this.dragleave)
    this.element.removeEventListener("drop", this.drop)
  }

  dragover = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    this.element.classList.add("ring-2", "ring-primary-400", "ring-inset")
  }

  dragleave = (e) => {
    if (!this.element.contains(e.relatedTarget)) {
      this.element.classList.remove("ring-2", "ring-primary-400", "ring-inset")
    }
  }

  drop = async (e) => {
    e.preventDefault()
    this.element.classList.remove("ring-2", "ring-primary-400", "ring-inset")

    let data
    try {
      data = JSON.parse(e.dataTransfer.getData("application/json"))
    } catch {
      return
    }

    const appointmentId = data?.appointmentId
    if (!appointmentId || !this.dateValue || !this.reschedulePathTemplateValue) return

    const grabOffsetY = data.grabOffsetY ?? 0
    const card = this.findCard(appointmentId)

    // Tell the draggable controller we handled this card (prevents dragend restore)
    this.markDraggableHandled(card)

    const { snappedMinutes, time } = this.computeTimeFromDrop(e, grabOffsetY)
    const targetDate = this.dateValue

    const originalState = card ? this.captureCardState(card) : null

    if (card) {
      this.optimisticUpdate(card, snappedMinutes, data.durationMinutes, time)
    }

    const url = this.reschedulePathTemplateValue.replace("__ID__", appointmentId)
    const csrfToken = this.csrfTokenValue || document.querySelector('[name="csrf-token"]')?.content || ""

    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ date: targetDate, time }),
      })
      const json = await res.json().catch(() => ({}))

      if (res.ok && json.success) {
        this.reconcileGrid()
      } else {
        if (originalState) this.revertCard(card, originalState)
        this.showError(json.error || "Could not reschedule appointment")
      }
    } catch (err) {
      if (originalState) this.revertCard(card, originalState)
      this.showError("Could not reschedule appointment")
    }
  }

  // --- Time calculation ---

  computeTimeFromDrop(e, grabOffsetY) {
    const rect = this.element.getBoundingClientRect()
    const hourHeight = this.hourHeightValue || DEFAULT_HOUR_HEIGHT

    // e.clientY - rect.top = cursor Y relative to column top (viewport coords,
    // so scroll is already accounted for by getBoundingClientRect).
    // Subtract grabOffsetY to get the intended position of the card's TOP edge.
    const cardTopY = (e.clientY - rect.top) - grabOffsetY
    const rawMinutes = Math.max(0, (cardTopY / hourHeight) * 60)
    const snappedMinutes = this.snapToQuarter(rawMinutes)

    const hours = Math.floor(snappedMinutes / 60)
    const mins = snappedMinutes % 60
    const time = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`

    return { snappedMinutes, time }
  }

  snapToQuarter(minutes) {
    const snapped = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES
    return Math.max(0, Math.min(TOTAL_MINUTES - SNAP_MINUTES, snapped))
  }

  // --- DOM helpers ---

  findCard(appointmentId) {
    return document.querySelector(
      `[data-appointment-draggable-appointment-id-value="${appointmentId}"]`
    )
  }

  markDraggableHandled(card) {
    if (!card) return
    const controller = this.application.getControllerForElementAndIdentifier(
      card,
      "appointment-draggable"
    )
    if (controller) controller.markHandled()
  }

  captureCardState(card) {
    return {
      parent: card.parentElement,
      top: card.style.top,
      height: card.style.height,
      left: card.style.left,
      width: card.style.width,
      opacity: card.style.opacity,
      pointerEvents: card.style.pointerEvents,
    }
  }

  // --- Optimistic update ---

  optimisticUpdate(card, snappedMinutes, durationMinutes, timeStr) {
    const targetColumn = this.element
    const topPct = (snappedMinutes / TOTAL_MINUTES) * 100
    const duration = durationMinutes ?? 60
    const heightPct = (duration / TOTAL_MINUTES) * 100

    if (card.parentElement !== targetColumn) {
      targetColumn.appendChild(card)
      card.style.left = "0%"
      card.style.width = "99%"
    }

    card.style.top = `${topPct}%`
    card.style.height = `${heightPct}%`
    card.style.opacity = ""
    card.style.pointerEvents = ""

    this.updateCardTimeText(card, snappedMinutes, duration)
  }

  updateCardTimeText(card, startMinutes, durationMinutes) {
    const endMinutes = startMinutes + durationMinutes
    const startStr = this.formatTime(startMinutes)
    const endStr = this.formatTime(endMinutes)

    const timeEl = card.querySelector("[class*='text_secondary'], [class*='mt-0.5']")
    if (timeEl && timeEl.textContent.includes("–")) {
      timeEl.textContent = `${startStr} – ${endStr}`
    }
  }

  formatTime(totalMinutes) {
    const h = Math.floor(totalMinutes / 60) % 24
    const m = totalMinutes % 60
    const hour12 = h % 12 === 0 ? 12 : h % 12
    const ampm = h < 12 ? "AM" : "PM"
    return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`
  }

  // --- Revert ---

  revertCard(card, state) {
    if (!card || !state) return
    if (state.parent && state.parent !== card.parentElement) {
      state.parent.appendChild(card)
    }
    card.style.top = state.top
    card.style.height = state.height
    card.style.left = state.left
    card.style.width = state.width
    card.style.opacity = state.opacity ?? ""
    card.style.pointerEvents = state.pointerEvents ?? ""
  }

  // --- Grid reconciliation ---

  reconcileGrid() {
    const gridEl = document.getElementById("calendar_week_grid")
    const refreshUrl = gridEl?.dataset?.calendarGridRefreshUrl
    if (!refreshUrl) return

    // Quiet background refresh — card is already at the correct position,
    // so this just ensures text/overlaps are fully up to date.
    setTimeout(() => {
      fetch(refreshUrl, { headers: { Accept: "text/html" } })
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, "text/html")
          const frame = doc.querySelector("#calendar_week_grid")
          const target = document.getElementById("calendar_week_grid")
          if (frame && target) {
            // Preserve scroll position across the content swap
            const scrollContainer = target.querySelector("[data-controller*='calendar-scroll']")
            const scrollTop = scrollContainer?.scrollTop ?? 0
            target.innerHTML = frame.innerHTML
            const newScroll = target.querySelector("[data-controller*='calendar-scroll']")
            if (newScroll) newScroll.scrollTop = scrollTop
          }
        })
        .catch(() => {})
    }, 300)
  }

  // --- Feedback ---

  showError(message) {
    const toast = document.createElement("div")
    toast.className = "fixed bottom-4 right-4 px-4 py-2 rounded-lg bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 text-sm shadow-lg z-[100] max-w-sm"
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 5000)
  }
}
